import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { PropertyResponseType } from '@services';
import { collectDocumentationRows } from '@/shared/utils/property-detail-fields';
import { PropertiesService } from '@/services/requests/housien-services/PropertiesService';
import { ChevronDownIcon } from '@radix-ui/react-icons';
import { Menu, Text, UnstyledButton } from '@/shared/ui';
import { SectionCard, InfoRow, EditCardButton } from '../SectionCard';

interface DocumentationCardProps {
  property: PropertyResponseType;
  agencyId: string;
  propertyId: string;
  /** Reloads the property after the IPTU situation is saved. */
  onSaved: () => void;
  /** Opens the property editor on the documentation section. */
  onEdit?: () => void;
}

/** Recorded IPTU situations. Empty string is "not informed". */
const IPTU_STATUSES = ['', 'Em_dia', 'Em_atraso'] as const;

/**
 * Localizes an IPTU situation, treating the empty value as "not informed".
 */
function labelFor(status: string, t: (key: string) => string): string {
  return status
    ? t(`detailView.documents.iptuStatusOptions.${status}`)
    : t('detailView.documents.iptuStatusOptions.unknown');
}

/**
 * Colours the IPTU situation so an overdue tax reads as a problem.
 */
function iptuColor(status: string): string {
  if (status === 'Em_dia') return 'var(--status-success-text)';
  if (status === 'Em_atraso') return 'var(--accent-cobre)';
  return 'var(--text-tertiary-soft)';
}

/**
 * Sidebar card with legal registry metadata (matrícula, cartório, inscrição)
 * and the IPTU situation.
 *
 * The situation is edited here rather than in the full listing form: it is a
 * one-word fact that changes on its own schedule, and sending a broker
 * through the whole registration form to flip it is why it would never get
 * updated.
 *
 * @param props - The property, its identifiers and the reload callback.
 * Always rendered, so the registry data can be filled in from its pencil;
 * with nothing recorded it still offers the IPTU situation.
 *
 * @returns The rendered card.
 */
export const DocumentationCard: React.FC<DocumentationCardProps> = ({
  property,
  agencyId,
  propertyId,
  onSaved,
  onEdit,
}) => {
  const { t } = useTranslation('features/properties');
  const rows = collectDocumentationRows(property.documentation);
  const stored = property.pricing?.taxes?.iptu_status ?? '';
  const [saving, setSaving] = useState(false);

  const handleChange = async (next: string) => {
    setSaving(true);
    try {
      await PropertiesService.updateProperty(
        propertyId,
        { pricing: { taxes: { iptu_status: next || null } } } as never,
        agencyId,
      );
      onSaved();
    } catch {
      toast.error(t('detailView.documents.iptuSaveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard
      title={t('detailView.documents.registryTitle')}
      data-testid="property-documentation-card"
      titleAccessory={onEdit ? <EditCardButton onClick={onEdit} data-testid="property-documentation-edit" /> : undefined}
    >
      {rows.map((row) => (
        <InfoRow key={row.labelKey} label={t(row.labelKey)} value={row.value} />
      ))}

      <div className="flex items-center justify-between gap-[var(--space-3)] py-[var(--space-2)]">
        <Text
          component="span"
          className="min-w-0 text-[length:var(--text-body-sm)] leading-[var(--leading-snug)] text-[var(--text-tertiary)]"
        >
          {t('detailView.documents.iptuStatus')}
        </Text>
        <Menu position="bottom-end" width={200}>
          <Menu.Target>
            <UnstyledButton
              type="button"
              disabled={saving}
              aria-label={t('detailView.documents.iptuStatus')}
              data-testid="property-iptu-status"
              style={{ color: iptuColor(stored) }}
              className="flex max-w-[60%] flex-none items-center gap-[var(--space-1)] truncate rounded-(--radius-sm) px-[var(--space-1)] text-right text-[length:var(--text-body-sm)] [font-weight:var(--font-bold)] hover:bg-[var(--surface-hover)] disabled:opacity-50"
            >
              {labelFor(stored, t)}
              <ChevronDownIcon width={12} height={12} className="shrink-0 opacity-60" />
            </UnstyledButton>
          </Menu.Target>
          <Menu.Dropdown className="p-0">
            {IPTU_STATUSES.map((status) => (
              <Menu.Item
                key={status || 'none'}
                onClick={() => void handleChange(status)}
                data-testid={`property-iptu-option-${status || 'none'}`}
                className="rounded-none px-(--space-3) py-(--space-2-5)"
              >
                <Text
                  component="span"
                  style={{ color: iptuColor(status) }}
                  className="text-[length:var(--text-body-sm)] [font-weight:var(--font-medium)]"
                >
                  {labelFor(status, t)}
                </Text>
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
      </div>
    </SectionCard>
  );
};
